// data.ts - the two locked Solo teaching hands (DX7 built the stage, DX8 added the mirror hand, DX9
// fixed the per-hand hole cards). Transcribed 1:1 from the approved mockup
// (docs/ui-explorations/local-mode-read-first-v2.html: HANDS / H1_* / H2_STREETS). Authored teaching
// content, sanctioned by PRODUCT_NORTH_STAR §5.
//
// The two hands are a deliberate MIRROR pair: same top-pair-top-kicker strength, opposite villain,
// opposite correct river play. Hand 1 (A-J vs the Vacuum) is a lifelong caller who never bluffs - you
// FOLD the river jam. Hand 2 (A-Q vs the Firecracker) is a habitual bluffer - you CALL it. The lesson is
// read the PLAYER, not the board.
//
// Hand 1's street content + grade REUSE Diagnose's data (same A-J-vs-Vacuum hand), re-skinned with Solo's
// kicker/lede and a typed-price ANSWER. Hand 2 is authored here in full. Solo shows NO inline grade (the
// grade only feeds the end-of-hand report), so the grade payload is LEAN - just the correct action, the
// right-play label, and the wrong-branch move classes - no coach/debrief prose (that is Diagnose's job).
import { DIAGNOSE_STREETS, type ActionDef, type BetMathDef, type FamilyDef, type PlayerTag } from '../diagnose/data';
import { DIAGNOSE_GRADES, type StreetGrade } from '../grade/data';
import type { MoveClass } from '../grade/types';
import type { SoloFeltStreet } from '../engine/bridge';

/** The typed-price ANSWER for a street: the dollars to call + the required equity %. Null preflop (no
 *  price to pay - you are first in). The user types both; the grader scores the call exactly and the % to
 *  within 3 points (see grade.ts). */
export interface SoloPrice {
  call: number;
  pct: number;
}

/** The LEAN Solo grade for a street - everything the report needs and nothing more. Solo never shows an
 *  inline reveal, so there is no handText/coach/debrief here (unlike grade/data.ts). */
export interface SoloGrade {
  /** the correct action (drives right-vs-wrong). */
  correct: 'fold' | 'call' | 'raise';
  /** the right-play label shown in the report row ("Call", "Raise to isolate", "Fold"). */
  rightLabel: string;
  /** wrong branches keyed by the diverging action: the move class it grades to + its label. */
  wrong: Partial<Record<'fold' | 'call' | 'raise', { cls: MoveClass; label: string }>>;
}

/** Everything the Solo stage renders for one street. */
export interface SoloStreetData {
  id: 'preflop' | 'flop' | 'turn' | 'river';
  kicker: string;
  h2: string;
  lede: string;
  tagSub: string;
  /** null preflop (no board yet, so no hand-family weighting). */
  families: FamilyDef[] | null;
  actions: ActionDef[];
  /** null when the street has no raise option (river). */
  bet: BetMathDef | null;
  /** the felt presentation (board/pot/villain-bet/descriptor) for the oval. */
  felt: SoloFeltStreet;
  /** the price answer to type, or null preflop (no price this street). */
  price: SoloPrice | null;
  grade: SoloGrade;
}

/** One Solo teaching hand: hero's hole cards, the active opponent, the correct read tag, the hand-picker
 *  label, and the four streets. */
export interface SoloHand {
  hole: readonly [string, string];
  /** the active opponent seat (avatar/name/stack) rendered on the felt. */
  opp: { avatar: string; name: string; stack: string };
  /** the tag that reads this player right (Axis B ground truth). */
  correctTag: PlayerTag;
  /** the gold hand-picker pill copy: a small eyebrow + a one-line label. */
  pick: { k: string; label: string };
  streets: SoloStreetData[];
}

/** Derive the lean Solo grade from a full Diagnose StreetGrade (Hand 1 reuses Diagnose's grade payload). */
function leanGrade(g: StreetGrade): SoloGrade {
  const wrong: SoloGrade['wrong'] = {};
  (['fold', 'call', 'raise'] as const).forEach((k) => {
    const w = g.wrong[k];
    if (w) wrong[k] = { cls: w.cls, label: w.handLabel };
  });
  return { correct: g.correct, rightLabel: g.right.handLabel, wrong };
}

// ---------- Hand 1: A-J vs the Vacuum (same hand as Diagnose; you FOLD the river) ----------

const H1_KICKER = ['Fly it yourself', 'The flop', 'The turn', 'The river'];
const H1_LEDE = [
  'No prompts. Read the seat, then decide how to play A-J suited. You are first in, so there is no price to pay yet.',
  'J 8 4, dry. You hit top pair top kicker, then the Vacuum leads. Read him, work out the price, and choose, all on your own.',
  'The 2 is a blank and he fires again. Nothing is shown. You supply the read, the price, and the play.',
  'The 7 is another blank and he jams all in. Last call of the hand, and no number is given. You work it out.',
];
const H1_FELT: SoloFeltStreet[] = [
  { board: [], pot: 7, vbet: '', handDescriptor: 'ace-jack suited' },
  { board: ['Jd', '8c', '4s'], pot: 43, vbet: '$30', handDescriptor: 'top pair, top kicker' },
  { board: ['Jd', '8c', '4s', '2h'], pot: 103, vbet: '$40', handDescriptor: 'top pair, top kicker' },
  { board: ['Jd', '8c', '4s', '2h', '7d'], pot: 183, vbet: '$120', handDescriptor: 'top pair, top kicker' },
];
const H1_PRICE: (SoloPrice | null)[] = [null, { call: 30, pct: 29 }, { call: 40, pct: 22 }, { call: 120, pct: 28 }];

const HAND_1: SoloHand = {
  hole: ['As', 'Js'],
  opp: { avatar: 'V', name: 'Vacuum', stack: '$188' },
  correctTag: 'Vacuum',
  pick: { k: 'Hand 1', label: 'A-J vs the Vacuum' },
  streets: DIAGNOSE_STREETS.map((d, i) => ({
    id: d.id,
    kicker: H1_KICKER[i],
    h2: d.h2,
    lede: H1_LEDE[i],
    tagSub: d.tagSub,
    families: d.families,
    actions: d.actions,
    bet: d.bet,
    felt: H1_FELT[i],
    price: H1_PRICE[i],
    grade: leanGrade(DIAGNOSE_GRADES[i]),
  })),
};

// ---------- Hand 2: A-Q vs the Firecracker (the mirror; you CALL the river) ----------

/** The five hand families for Hand 2 (a wet Q-9-6 two-tone board: draws matter, so the weak-Q / draw
 *  detail differs from Hand 1). */
const H2_FAMILIES: FamilyDef[] = [
  { name: 'Nuts', detail: 'sets, two pair' },
  { name: 'Strong made', detail: 'a better queen' },
  { name: 'Medium pairs', detail: '9x, weak Q' },
  { name: 'Draws', detail: 'flush, straight' },
  { name: 'Air, bluff', detail: 'pure nothing' },
];

const HAND_2: SoloHand = {
  hole: ['As', 'Qs'],
  opp: { avatar: 'F', name: 'Firecracker', stack: '$466' },
  correctTag: 'Firecracker',
  pick: { k: 'Hand 2', label: 'A-Q vs the Firecracker' },
  streets: [
    {
      id: 'preflop',
      kicker: 'Fly it yourself',
      h2: 'A Firecracker sits on your left',
      lede:
        'No prompts. Read the seat, then decide how to play A-Q suited. You are first in, so there is no price to pay yet.',
      tagSub:
        'The player on your left keeps raising light and betting into everyone. What kind of player is he?',
      families: null,
      actions: [
        { key: 'fold', label: 'Fold', sub: 'give up A-Q suited', cls: 'fold' },
        { key: 'call', label: 'Just call', sub: 'see a cheap flop', cls: 'call' },
        { key: 'raise', label: 'Raise to isolate', sub: 'build a heads-up pot', cls: 'raise' },
      ],
      bet: {
        pfx: 'Raise to',
        pot: '$7',
        call: '$3',
        callLabel: 'To call',
        vbet: 'blinds',
        sub: 'A clean iso is about 5 to 6 big blinds. Bigger than a TV raise, so the field folds and you get him alone.',
        quick: [
          { amount: '$12', ratio: '4x' },
          { amount: '$15', ratio: '5x' },
          { amount: '$18', ratio: '6x' },
        ],
      },
      felt: { board: [], pot: 7, vbet: '', handDescriptor: 'ace-queen suited' },
      price: null,
      grade: {
        correct: 'raise',
        rightLabel: 'Raise to isolate',
        wrong: {
          fold: { cls: 'blun', label: 'Fold' },
          call: { cls: 'inacc', label: 'Just call' },
        },
      },
    },
    {
      id: 'flop',
      kicker: 'The flop',
      h2: 'He leads $24 into $32',
      lede:
        'Q 9 6, two diamonds. You flop top pair top kicker, then the Firecracker leads into you. Read him, work out the price, and choose, all on your own.',
      tagSub:
        'He just led into the preflop raiser, out of turn, something solid players almost never do. Confirm the read.',
      families: H2_FAMILIES,
      actions: [
        { key: 'fold', label: 'Fold', sub: 'give up top pair', cls: 'fold' },
        { key: 'call', label: 'Call $24', sub: 'keep his bluffs in', cls: 'call' },
        { key: 'raise', label: 'Raise', sub: 'fold out his air', cls: 'raise' },
      ],
      bet: {
        pfx: 'Raise to',
        pot: '$32',
        call: '$24',
        callLabel: 'To call',
        vbet: '$24',
        sub: 'If you raise you have to build the number. But ask first what worse hand pays it.',
        quick: [
          { amount: '$70', ratio: '2x' },
          { amount: '$85', ratio: '2.5x' },
          { amount: '$100', ratio: '3x' },
        ],
      },
      felt: { board: ['Qd', '9c', '6d'], pot: 32, vbet: '$24', handDescriptor: 'top pair, top kicker' },
      price: { call: 24, pct: 30 },
      grade: {
        correct: 'call',
        rightLabel: 'Call',
        wrong: {
          fold: { cls: 'mist', label: 'Fold' },
          raise: { cls: 'mist', label: 'Raise' },
        },
      },
    },
    {
      id: 'turn',
      kicker: 'The turn',
      h2: 'He barrels again, $60 into $80',
      lede:
        'The 2 bricks and he barrels again. Nothing is shown. You supply the read, the price, and the play.',
      tagSub: 'Same player, betting into you twice now. Has your read changed?',
      families: H2_FAMILIES,
      actions: [
        { key: 'fold', label: 'Fold', sub: 'too soon', cls: 'fold' },
        { key: 'call', label: 'Call $60', sub: 'plan the river', cls: 'call' },
        { key: 'raise', label: 'Raise', sub: 'blow him off it', cls: 'raise' },
      ],
      bet: {
        pfx: 'Raise to',
        pot: '$80',
        call: '$60',
        callLabel: 'To call',
        vbet: '$60',
        sub: 'You could raise, but why fold out the bluffs you are beating?',
        quick: [
          { amount: '$150', ratio: '2x' },
          { amount: '$180', ratio: '2.5x' },
          { amount: '$220', ratio: '3x' },
        ],
      },
      felt: { board: ['Qd', '9c', '6d', '2s'], pot: 80, vbet: '$60', handDescriptor: 'top pair, top kicker' },
      price: { call: 60, pct: 30 },
      grade: {
        correct: 'call',
        rightLabel: 'Call',
        wrong: {
          fold: { cls: 'inacc', label: 'Fold' },
          raise: { cls: 'mist', label: 'Raise' },
        },
      },
    },
    {
      id: 'river',
      kicker: 'The river',
      h2: 'He jams all in, $150 into $200',
      lede:
        'The 4 bricks. Every draw missed and he jams all in. Last call of the hand, and no number is given. You work it out.',
      tagSub:
        'Third barrel, all in, from a habitual bluffer on a board where everything missed. What is he?',
      families: H2_FAMILIES,
      actions: [
        { key: 'fold', label: 'Fold', sub: 'lay it down', cls: 'fold' },
        { key: 'call', label: 'Call $150', sub: 'pay off the bluff-catch', cls: 'call' },
      ],
      bet: null,
      felt: {
        board: ['Qd', '9c', '6d', '2s', '4c'],
        pot: 200,
        vbet: '$150',
        handDescriptor: 'top pair, top kicker',
      },
      price: { call: 150, pct: 30 },
      grade: {
        correct: 'call',
        rightLabel: 'Call',
        wrong: {
          fold: { cls: 'blun', label: 'Fold' },
        },
      },
    },
  ],
};

/** The two Solo teaching hands, in hand-picker order. */
export const SOLO_HANDS: SoloHand[] = [HAND_1, HAND_2];
